import React, { useEffect, useState } from 'react';
import useRoute from '@/Hooks/useRoute';
import ApplicationMark from '@/Components/ApplicationMark';
import NavLink from '@/Components/NavLink';
import IconBook from '@/svgs/book-read.svg?react';
import IconCollapse from '@/svgs/layout-left.svg?react';
import IconMoney from '@/svgs/money-dollar-circle.svg?react';
import IconCode from '@/svgs/code-box.svg?react';
import IconHelp from '@/svgs/customer-service.svg?react';
import IconSignIn from '@/svgs/signin.svg?react';
import IconPricing from '@/svgs/pricetag.svg?react';
import { cn } from '@/utils';
import AlertSignup from './AlertSignup';
import ButtonCreateFile from './ButtonCreateFile';
import { useIntercom } from 'react-use-intercom';

export default function AppNav() {
  const route = useRoute();
  const [navCollapsed, setNavCollapsed] = useState<boolean | null>(null);

  const { show, boot } = useIntercom();

  function launchIntercom() {
    boot({
      alignment: 'left',
      horizontalPadding: (navCollapsed ? 64 : 240) + 20,
    });
    show();
  }

  const isSnippetViewVisible = route().current('snippet.show');

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 992) {
        setNavCollapsed(true); // Set collapsed state if viewport width is less than or equal to 992px
      } else {
        setNavCollapsed(false); // Set expanded state if viewport width is greater than 992px
      }
    };

    handleResize();

    // Add event listener for window resize to update navCollapsed state
    window.addEventListener('resize', handleResize);

    return () => {
      // Remove event listener on component cleanup
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      className={cn(
        'h-full overflow-y-auto bg-brand-25 text-lg text-brand-dark-25 transition-all duration-200 ease-in-out dark:bg-brand-dark-75 dark:text-brand-dark-25',
        {
          'w-60': navCollapsed == false,
          'collapsed w-16': navCollapsed == true,
        },
        {
          'fixed z-20 lg:relative': isSnippetViewVisible,
        }
      )}
    >
      <div className="flex h-full flex-col">
        <div
          className={cn('my-6 flex justify-between px-3', {
            'flex-col items-center': navCollapsed == true,
          })}
        >
          <NavLink
            href={route('dashboard')}
            className="flex gap-2"
            forceUrlDomain="app"
          >
            <ApplicationMark />
          </NavLink>

          <button
            onClick={() => setNavCollapsed(!navCollapsed)}
            className={cn('transition', {
              'mt-6 scale-[-1]': navCollapsed == true,
              'scale-1': navCollapsed == false,
            })}
          >
            <IconCollapse />
          </button>
        </div>
        <div className="my-6 px-3">
          <ButtonCreateFile route={route('login')} />
        </div>
        <nav className="flex flex-1 flex-col space-y-3 px-3">
          <NavLink
            href={route('snippets.index')}
            active={route().current('snippets.index')}
            forceUrlDomain="website"
          >
            <IconCode className="h-6 w-6" />
            <span className="collapsable">Snippets</span>
          </NavLink>
          <NavLink
            href="https://codewp.ai/pricing"
            active={false}
            className="w-full"
            as="externalLink"
            target="_self"
          >
            <IconPricing className="h-6 w-6" />
            <span className="collapsable">Pricing</span>
          </NavLink>
        </nav>

        <div className="my-12">
          {route().current('snippet.show') && (
            <div className="mx-4">
              <AlertSignup />
            </div>
          )}

          <div className="flex flex-1 flex-col space-y-3 px-3">
            <NavLink href="https://codewp.ai/affiliates/" active={false}>
              <IconMoney className="h-6 w-6" />
              <span className="collapsable">Affiliate Program</span>
            </NavLink>
            <NavLink onClick={launchIntercom} active={false}>
              <IconHelp className="h-6 w-6" />
              <span className="collapsable">Help & Support</span>
            </NavLink>
            <NavLink href="https://codewp.ai/terms-of-service" active={false}>
              <IconBook className="h-6 w-6" />
              <span className="collapsable">Legal & Terms</span>
            </NavLink>
            <NavLink href={route('login')} active={false} forceUrlDomain="app">
              <IconSignIn className="h-6 w-6" />
              <span className="collapsable">Sign In</span>
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
}
